import React from 'react'

import { XStack, YStack, View } from 'tamagui'

import { Text } from '../Text'
import { Icon } from '../Icon'
import { UserPrimaryPhoto } from '../User/UserPrimaryPhoto'
import { SearchGroupItem } from './SearchGroup'
import type { QuickAction } from './searchQuickActions'
import type { UserPrimaryPhotoNew_user$key } from '../User/__generated__/UserPrimaryPhotoNew_user.graphql'

const QuickActionIcon = ({
  action,
  viewerUser,
  currentTheme,
  size,
}: {
  action: QuickAction
  viewerUser?: UserPrimaryPhotoNew_user$key | null
  currentTheme?: 'light' | 'dark'
  size: '$5' | '$6'
}) => {
  // Profile action: show user's actual photo when available
  // Match search result photo sizing: $12 (48px) in full mode, 20px in compact
  if (action.id === 'profile' && viewerUser) {
    return (
      <UserPrimaryPhoto
        user={viewerUser}
        size={size === '$5' ? '$5' : '$12'}
        borderWidth={0}
      />
    )
  }

  // Home action: use guild logo
  if (action.id === 'home') {
    return <Icon name='guild-logo' size={size === '$5' ? '$5' : '$8'} color='$color' />
  }

  // Toggle theme: show contextual icon based on current theme
  if (action.id === 'toggle-theme') {
    const iconName = currentTheme === 'dark' ? 'weather-sun' : 'weather-moon'
    return <Icon name={iconName} size={size} color='$color' />
  }

  return <Icon name={action.icon} size={size} color='$color' />
}

export const QuickActionRow = ({
  action,
  index,
  onSelect,
  compact,
  viewerUser,
  currentTheme,
}: {
  action: QuickAction
  index: number
  onSelect: () => void
  /** Compact mode for suggested actions (default view). Full mode for search results. */
  compact?: boolean
  viewerUser?: UserPrimaryPhotoNew_user$key | null
  currentTheme?: 'light' | 'dark'
}) => {
  return (
    <SearchGroupItem index={index} onSelect={onSelect}>
      {compact ? (
        <XStack gap='$3' alignItems='center'>
          <QuickActionIcon action={action} viewerUser={viewerUser} currentTheme={currentTheme} size='$5' />
          <Text fontSize='$5'>{action.label}</Text>
        </XStack>
      ) : (
        <XStack gap='$3' alignItems='center'>
          {action.id === 'profile' && viewerUser ? (
            <QuickActionIcon action={action} viewerUser={viewerUser} currentTheme={currentTheme} size='$6' />
          ) : (
            <View
              width={48}
              height={48}
              borderRadius='$4'
              backgroundColor='$background'
              borderWidth={1}
              borderColor='$borderColor'
              alignItems='center'
              justifyContent='center'
            >
              <QuickActionIcon action={action} viewerUser={viewerUser} currentTheme={currentTheme} size='$6' />
            </View>
          )}
          <YStack flex={1} gap='$1'>
            <Text fontSize='$5' fontWeight='$6' numberOfLines={1}>
              {action.label}
            </Text>
          </YStack>
        </XStack>
      )}
    </SearchGroupItem>
  )
}
